<template>
    <b-card :title="title" class="lines-card" :class="{ 'lines-card--invalid': invalid }">

        <FormGroup no-label class="mb-10">
            <template #input>
                <v-select v-model="selected"
                          :options="options"
                          :placeholder="placeholder"
                          label="name"
                          :filterable="false"
                          :disabled="disabled"
                          :append-to-body="isMobile"
                          @search="onSearch"
                          @input="onSelect">
                    <template #option="option">
                        <NameNoOption :name="option.name" :no="option.no" />
                    </template>
                    <template #selected-option="option">
                        <NameNoOption :name="option.name" :no="option.no" />
                    </template>
                    <template #no-options>Ingen resultater</template>
                </v-select>
            </template>
        </FormGroup>

        <EditableTable :items="value"
                       :columns="columns"
                       key-field="no"
                       :force-stacked="narrow"
                       removable
                       :disabled="disabled"
                       :empty-text="emptyText"
                       table-class="lines-table"
                       @remove="index => $emit('remove', index)">

            <template #cell(no)="{ item }">
                <span class="font-weight-bold">{{ item.no }}</span>
            </template>

            <template #cell(description)="{ item }">
                <b-form-textarea v-if="lazy" v-model.lazy="item.description" size="sm" rows="2" :disabled="disabled"></b-form-textarea>
                <b-form-textarea v-else v-model="item.description" size="sm" rows="2" :disabled="disabled"></b-form-textarea>
            </template>

            <template #cell(amount)="{ item }">
                <div class="lines-card__amount">
                    <b-input-group size="sm" class="lines-card__stepper">
                        <b-input-group-prepend>
                            <b-button variant="outline-secondary" class="btn-input-border lines-card__step" :disabled="disabled" @click="step(item, -1)">
                                <feather-icon icon="MinusIcon"></feather-icon>
                            </b-button>
                        </b-input-group-prepend>
                        <b-form-input v-model="item.amount" size="sm" class="text-center lines-card__qty" inputmode="decimal" :disabled="disabled"></b-form-input>
                        <b-input-group-append>
                            <b-button variant="outline-secondary" class="btn-input-border lines-card__step" :disabled="disabled" @click="step(item, 1)">
                                <feather-icon icon="PlusIcon"></feather-icon>
                            </b-button>
                        </b-input-group-append>
                    </b-input-group>

                    <b-input-group v-if="hasUnitPrice(item)" size="sm" prepend="Stk. pris" class="lines-card__unit-price">
                        <b-form-input v-model.lazy="item.unitPrice" size="sm" class="text-center" inputmode="decimal" :disabled="disabled"></b-form-input>
                    </b-input-group>

                    <b-form-checkbox v-model="item.invoice" switch :disabled="disabled" class="lines-card__invoice">
                        Skal faktureres
                    </b-form-checkbox>
                </div>
            </template>
        </EditableTable>

        <template v-if="suggestions && suggestions.length">
            <hr />

            <h5 class="mb-10">{{ suggestionsTitle }}</h5>
            <EditableTable :items="suggestions"
                           :columns="suggestionColumns"
                           key-field="no"
                           :force-stacked="narrow"
                           removable
                           :disabled="disabled">
                <template #cell(no)="{ item }">
                    <span class="font-weight-bold">{{ item.no }}</span>
                </template>
                <template #row-actions="{ item }">
                    <b-button variant="outline-success" size="sm" class="text-nowrap tap-target-sm" :disabled="disabled" @click="$emit('add', item)">
                        <feather-icon icon="PlusIcon" class="mr-3"></feather-icon>Tilføj
                    </b-button>
                </template>
            </EditableTable>
        </template>
    </b-card>
</template>

<script>
    /*
     * "Resurser" / "Reservedele" card: search box + editable lines + optional suggestions.
     * The parent owns the array (v-model) and the network calls:
     *
     *   <LinesCard v-model="item.resources" title="Resurser" placeholder="Find resurse"
     *              :options="resources" :fetch="fetchResources" :suggestions="filteredResources"
     *              :disabled="ended" lazy @select="selectResource" @add="addResource"
     *              @remove="i => item.resources.splice(i, 1)" />
     */
    export default {
        props: {
            value: {
                type: Array,
                default: () => []
            },
            options: {
                type: Array,
                default: () => []
            },
            fetch: {
                type: Function,
                default: null
            },
            suggestions: {
                type: Array,
                default: () => []
            },
            title: {
                type: String,
                default: 'Resurser'
            },
            placeholder: {
                type: String,
                default: 'Find...'
            },
            suggestionsTitle: {
                type: String,
                default: 'Foreslåede'
            },
            emptyText: {
                type: String,
                default: 'Ingen linjer tilføjet'
            },
            disabled: {
                type: Boolean,
                default: false
            },
            lazy: {
                type: Boolean,
                default: false
            },
            invalid: {
                type: Boolean,
                default: false
            },
            amountFormat: {
                type: String,
                default: '0.00'
            },
            // item numbers that get a unit price input (spare part 991110 = "diverse")
            unitPriceFor: {
                type: Array,
                default: () => []
            }
        },
        data: () => ({
            selected: null,
            narrow: null,
            resizeObserver: null,
            columns: [
                { key: 'no', label: 'Varenr.', width: '120px', title: true },
                { key: 'description', label: 'Beskrivelse', block: true },
                { key: 'amount', label: 'Antal', width: '210px', class: 'text-right', block: true }
            ],
            suggestionColumns: [
                { key: 'no', label: 'Varenr.', width: '120px', title: true },
                { key: 'name', label: 'Beskrivelse' }
            ]
        }),
        mounted() {
            // The card lives in a side column: switch to stacked rows when it gets narrow,
            // regardless of the viewport breakpoint.
            var el = this.$el
            if (el && el.nodeType === 1 && typeof ResizeObserver !== 'undefined') {
                this.resizeObserver = new ResizeObserver(entries => {
                    var width = entries[0].contentRect.width
                    this.narrow = width < 560
                })
                this.resizeObserver.observe(el)
            }
        },
        beforeDestroy() {
            if (this.resizeObserver) {
                this.resizeObserver.disconnect()
            }
        },
        methods: {
            onSearch(search, loading) {
                if (this.fetch) {
                    this.fetch(search, loading)
                }
            },
            onSelect(option) {
                if (!option) {
                    return
                }
                this.$emit('select', option)
                this.$nextTick(() => {
                    this.selected = null
                })
            },
            step(item, delta) {
                item.amount = this.$numeral(item.amount).add(delta).format(this.amountFormat)
            },
            hasUnitPrice(item) {
                return this.unitPriceFor.indexOf(item.no) !== -1
            }
        }
    }
</script>

<style lang="scss" scoped>
    @import '~@core/scss/base/bootstrap-extended/include';

    .lines-card--invalid {
        border: 2px solid rgba($danger, 0.6);
    }

    .lines-card ::v-deep .lines-table {
        border: 2px solid rgba($success, 0.45);
        border-radius: $border-radius;
    }

    .lines-card__amount {
        display: inline-flex;
        flex-direction: column;
        align-items: flex-end;
        gap: 6px;
    }

    .lines-card__stepper {
        width: auto;
        flex-wrap: nowrap;
    }

    .lines-card__qty {
        width: 72px;
        flex: 0 0 72px;
    }

    .lines-card__step {
        min-width: 40px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
    }

    .lines-card__unit-price {
        width: auto;

        .form-control {
            width: 90px;
            flex: 0 0 90px;
        }
    }

    .tap-target-sm {
        min-height: 36px;
    }

    @include media-breakpoint-down(sm) {
        .lines-card__amount {
            align-items: stretch;
            width: 100%;
        }

        .lines-card__stepper {
            width: 100%;

            .lines-card__qty {
                flex: 1 1 auto;
                width: auto;
            }
        }

        .lines-card__step {
            min-width: 48px;
            min-height: 44px;
        }

        .lines-card__unit-price {
            width: 100%;

            .form-control {
                flex: 1 1 auto;
                width: auto;
            }
        }

        .lines-card__invoice {
            padding-top: 4px;
        }
    }
</style>
